import pages from "./pages.json";
import type { PageBlock } from "../components/PageBody";
import { getUiStrings } from "./uiStrings";
import { buildDetailMeta } from "./crosshairInfo";
import type { Locale } from "./locales";

/**
 * Typed access to the shared static page metadata.
 *
 * These strings used to be written twice — once as PageSeo props in each route
 * component and once in SeoController.TryGetStaticPageMeta on the C# side. With three
 * locales planned that would have become six copies of every title. The JSON is now the
 * single source; the web server reads the same file for its first-HTML injection.
 */

export type PageMeta = {
    title: string;
    description: string;
    keywords?: string;
    ogType?: "website" | "article";
    /** Heading and lead used by the server-rendered body (web/seo-content.mjs). */
    h1?: string;
    intro?: string;
    /**
     * Page copy. When present the route renders it via <PageBody />; when absent the
     * component falls back to its built-in English JSX. Routes still on that fallback
     * ship no copy at all to crawlers, so moving their text in here is the remaining
     * cleanup — /valorant-crosshair-guide was the first one done.
     */
    body?: PageBlock[];
    /** Article pages only (the guide): ISO dates for the Article schema, per locale. */
    datePublished?: string;
    dateModified?: string;
};

export const DEFAULT_LOCALE = "en";

type LocaleMap = Record<string, Record<string, PageMeta>>;

const byLocale = pages as unknown as LocaleMap;

/**
 * Metadata for a route, or null when the route has none (auth screens, 404).
 * Falls back to the default locale so a partially translated locale still renders.
 */
export function getPageMeta(path: string, locale: string = DEFAULT_LOCALE): PageMeta | null {
    return byLocale[locale]?.[path] ?? byLocale[DEFAULT_LOCALE]?.[path] ?? null;
}

/**
 * Spread straight into <PageSeo />. Throws on an unknown route rather than silently
 * rendering an untitled page — a missing entry is a wiring mistake, not a runtime state.
 */
export function pageSeoProps(path: string, locale: string = DEFAULT_LOCALE): PageMeta {
    const meta = getPageMeta(path, locale);
    if (!meta) {
        throw new Error(`No SEO metadata for route "${path}" — add it to src/seo/pages.json`);
    }
    return meta;
}

/**
 * Title, description and keywords for a crosshair detail page.
 *
 * Detail pages are the one route whose copy comes from the database rather than
 * pages.json, so the templates live in uiStrings.json instead. The builder itself is
 * shared with web/seo-content.mjs, which produces the same strings for the first HTML
 * response — the two used to be hand-kept copies.
 */
export function crosshairDetailMeta(
    crosshair: { name: string; slug?: string; code: string; isPro?: boolean; isFun?: boolean },
    locale: Locale
): { title: string; description: string; keywords: string } {
    const ui = getUiStrings(locale);
    return buildDetailMeta(crosshair, ui.detail, ui.settings);
}
